/**
 * SeedDrawerContent Component
 *
 * Routes and renders tab content for the SeedDrawer based on the active tab.
 * Handles different tab types and seed-type-specific content.
 *
 * This component acts as a content router that:
 * - Switches between different tab views based on activeTab value
 * - Conditionally renders seed-type-specific tabs (webapp overview/application)
 * - Manages loading states while data is being fetched
 * - Passes appropriate data to each tab component
 *
 * @component
 */

import { useCallback } from 'react';
import { toast } from 'sonner';

import { Container } from '@/components/legacy-ui';
import { Skeleton } from '@/components/ui/skeleton';
import { useUpdateSeed } from '@/hooks/useSeeds';
import { VulnerabilitiesTab } from '@/sections/asset/components/tabs';
import { NotesTab } from '@/sections/NotesTab';
import {
  ApplicationTab,
  DetailsTab,
  DomainHistoryTab,
  SeedHistoryTab,
  WebApplicationOverviewTab,
} from '@/sections/seeds/components/drawer/tabs';
import type { WhoisHistoryStatus } from '@/sections/seeds/hooks/drawer/useSeedDrawerData';
import { ProvenanceNode } from '@/sections/seeds/utils/provenanceProcessing';
import { Access, Seed } from '@/types';
import { Regex } from '@/utils/regex.util';

interface SeedDrawerContentProps {
  /** The seed to display content for */
  seed: Seed;
  /** Currently active tab identifier */
  activeTab: string;
  /** Formatted details content (WHOIS data or IP information) */
  displayDetails?: string;
  /** Array of historical WHOIS records for domain history tab */
  history?: unknown[];
  /** Information about how the seed was discovered/originated */
  originationInfo: string[];
  /** Discovery chain from DISCOVERED relationship traversal */
  provenanceChain: ProvenanceNode[];
  /** Access data for resolving user emails in history timeline */
  accesss: Access[];
  /** Loading status for details data */
  detailsStatus: 'error' | 'success' | 'pending';
  /** Loading status for history data */
  historyStatus: 'error' | 'success' | 'pending';
  /** WHOIS history file status from structured envelope */
  whoisHistoryStatus?: WhoisHistoryStatus;
  /** Domain has no registration at its registry (definitive backend signal) */
  isDomainUnregistered?: boolean;
  /** Callback to open comment modal for adding notes (omit to hide Add Note button) */
  onCommentClick?: () => void;
  /**
   * Seed drawer tab change handler (the useSeedDrawerTab setter). Threaded to
   * the webapp overview so cross-tab links (e.g. Affiliation Validation "View
   * Details") switch seed drawer tabs instead of writing asset drawer params.
   */
  onTabChange?: (tab: string) => void;
}

/**
 * SeedDrawerContent routes to the appropriate tab content
 *
 * The raw activeTab value (URL-persisted, so possibly stale for this seed's
 * type) is first normalized into a `contentTab` that is valid for the seed:
 *
 * Web application seeds:
 * - 'overview': Renders the asset drawer's AssetOverview (same Neo4j node)
 * - 'application': Shows webapp sitemap/application content
 * - 'vulnerabilities': Shows the asset drawer's vulnerabilities table
 * - 'seedHistory': Shows seed lifecycle timeline
 * - 'seedNotes': Shows notes associated with the seed
 *
 * Domain / IP seeds:
 * - 'details': Shows WHOIS/domain details and origination info
 * - 'domainHistory': Shows historical WHOIS data changes (domain only)
 * - 'seedHistory': Shows seed lifecycle timeline
 * - 'seedNotes': Shows notes associated with the seed
 *
 * Legacy tab names ('authentication', 'sitemap') are mapped to their
 * renamed equivalents ('overview', 'application') for stale URL compatibility.
 * A stale tab from ANOTHER seed class (e.g. `?seedDrawerTab=overview` left
 * behind by a webapp seed, then a domain seed is opened; or `domainHistory`
 * left by a domain seed, then an IP seed is opened) is clamped to the seed
 * class's own fallback tab — see normalization below.
 *
 * Loading states are managed globally and applied to all tabs except notes.
 *
 * @param seed - The seed object containing all metadata
 * @param activeTab - The currently selected tab identifier
 * @param displayDetails - Formatted details string for details tab
 * @param history - WHOIS history records for domain history tab
 * @param originationInfo - Discovery source information
 * @param accesss - Access objects for user email resolution
 * @param detailsStatus - Loading state for details data
 * @param historyStatus - Loading state for history data
 * @param onCommentClick - Handler for opening the comment modal
 * @param onTabChange - Seed drawer tab setter threaded to the webapp overview
 */
export function SeedDrawerContent({
  seed,
  activeTab,
  displayDetails,
  history,
  originationInfo,
  provenanceChain,
  accesss,
  detailsStatus,
  historyStatus,
  whoisHistoryStatus,
  isDomainUnregistered,
  onCommentClick,
  onTabChange,
}: SeedDrawerContentProps) {
  const { mutateAsync: updateSeed } = useUpdateSeed();

  const isWebapp = seed.class === 'webapplication';

  // Same IP detection as SeedDrawerTabs — the tab STRIP and this content
  // router must classify the seed identically, or a stale URL param could
  // render content for a tab the strip doesn't even offer.
  const isIP = seed.identifier
    ? Regex.IPV4.test(seed.identifier) || Regex.IPV6.test(seed.identifier)
    : false;

  // Handler for removing notes from seeds (uses different mutation than other entity types)
  const handleRemoveNote = useCallback(
    async (index: number) => {
      await updateSeed({
        type: seed.class === 'webapplication' ? 'webapplication' : 'asset',
        key: seed.key,
        identifier: seed.identifier,
        remove: index,
        showSnackbar: false,
      });
      toast.success('Note removed successfully');
    },
    [updateSeed, seed.class, seed.key, seed.identifier]
  );

  // The tab a stale/unknown value degrades to. Matches the tab STRIP's own
  // `tabs[0]` fallback in SeedDrawerTabs so strip and content stay consistent.
  const fallback: 'overview' | 'details' = isWebapp ? 'overview' : 'details';

  type ContentTab =
    | 'overview'
    | 'details'
    | 'application'
    | 'vulnerabilities'
    | 'domainHistory'
    | 'seedHistory'
    | 'seedNotes';

  // Normalize the raw activeTab (URL-persisted, so possibly left behind by a
  // seed of ANOTHER class) into a tab valid for THIS seed's class. This is the
  // mandatory clamp: a domain/IP seed with a stale 'overview'/'authentication'/
  // 'vulnerabilities' param must NEVER render the webapp-only branches; a
  // webapp seed with a stale 'details'/'domainHistory' param gets its
  // overview; and an IP seed with a stale 'domainHistory' param (left by a
  // domain seed) gets its details — only domain seeds have a Domain History
  // tab.
  const normalizeTab = (tab: string): ContentTab => {
    switch (tab) {
      // 'authentication' is the legacy name for 'overview' (stale URL compat).
      // For domain/IP seeds all three collapse to 'details' via the fallback.
      case 'authentication':
      case 'overview':
      case 'details':
        return fallback;

      // 'sitemap' is the legacy name for 'application' (stale URL compat)
      case 'sitemap':
      case 'application':
        return isWebapp ? 'application' : fallback;

      case 'vulnerabilities':
        return isWebapp ? 'vulnerabilities' : fallback;

      // Domain seeds only: SeedDrawerTabs gives IP seeds no Domain History
      // tab (their strip is details/seedHistory/seedNotes), so webapp seeds
      // clamp to 'overview' and IP seeds to 'details' via the fallback.
      case 'domainHistory':
        return !isWebapp && !isIP ? 'domainHistory' : fallback;

      case 'seedHistory':
      case 'seedNotes':
        return tab;

      // Unknown / removed / renamed tab (e.g. a stale `?seedDrawerTab=discovery`
      // bookmark after the drawer Discovery tab was removed) degrades to the
      // universal default CONTENT — the same tab the STRIP falls back to via
      // `tabs[0]` (SeedDrawerTabs). This keeps strip and content consistent
      // instead of showing a "Tab not found" error next to a highlighted first
      // tab. Mirrors the legacy-name mapping above.
      default:
        return fallback;
    }
  };

  const contentTab = normalizeTab(activeTab);

  // Render tab content based on the normalized contentTab
  const renderTabContent = () => {
    switch (contentTab) {
      case 'overview':
        // Webapp seeds only (the normalizer guarantees it): the seed and its
        // Assets-table row are the same node, so render the asset overview.
        return (
          <WebApplicationOverviewTab seed={seed} onTabChange={onTabChange} />
        );

      case 'details':
        return (
          <DetailsTab
            seed={seed}
            displayDetails={displayDetails}
            originationInfo={originationInfo}
            provenanceChain={provenanceChain}
            isDomainUnregistered={isDomainUnregistered}
          />
        );

      case 'application':
        // Application view: left sub-nav switching Sitemap ⇄ REST API Endpoints
        // (both reused from the asset section, fed the seed's fields).
        return <ApplicationTab seed={seed} />;

      case 'vulnerabilities':
        // The real asset-drawer vulnerabilities table. The `asset` prop is
        // deliberately omitted: passing it would require a Seed→Asset cast and
        // would widen scoping to the application rollup; per-asset scoping via
        // assetKey is correct for a seed drawer.
        return <VulnerabilitiesTab assetKey={seed.key} />;

      case 'domainHistory':
        return (
          <DomainHistoryTab
            seed={seed}
            history={history}
            whoisHistoryStatus={whoisHistoryStatus}
            isDomainUnregistered={isDomainUnregistered}
          />
        );

      case 'seedHistory':
        return <SeedHistoryTab seed={seed} accesss={accesss} />;

      case 'seedNotes':
        return (
          <NotesTab
            resource={seed}
            resourceType="Seed"
            accesss={accesss}
            onRemoveNote={onCommentClick ? handleRemoveNote : undefined}
            onOpenCommentModal={onCommentClick}
          />
        );
    }
  };

  // Full-bleed tabs own their padding: AssetOverview bakes in its own `p-6`,
  // and the Application / Vulnerabilities tables reach the panel edges (same
  // `p-0` container as the asset drawer). Computed from contentTab — not
  // activeTab — so DetailsTab fallbacks stay padded.
  const fullBleed =
    contentTab === 'overview' ||
    contentTab === 'application' ||
    contentTab === 'vulnerabilities';

  return (
    <Container
      // Container bakes in `p-6`, so override it with `p-0` for full-bleed
      // tabs (twMerge wins on the later class); other tabs keep `p-6`.
      className={fullBleed ? 'p-0' : 'p-6'}
      tabIndex={-1}
      // The normalized tab, not the raw activeTab: padding and content are
      // both computed from contentTab, so the id must match what is rendered.
      id={contentTab}
    >
      {detailsStatus === 'pending' || historyStatus === 'pending' ? (
        <Skeleton shape="block" className="m-6 min-h-48 w-[calc(100%-48px)]" />
      ) : (
        renderTabContent()
      )}
    </Container>
  );
}
